import { Currency, Dinero, transformScale } from "dinero.js"
import { useMemo } from "react"
import { createRoundingMoneyFormatter, createRoundingNumberFormatter, toDecimalParts } from "./currencyLib"
import { useAppSelector } from "../store/storeHooks"
import { selectSelectedLocale } from "../localization/localizationSelectors"
import { AmountUnit } from "./currencyTypes"
import { useAppLocalization } from "../localization/components/AppLocalizationProvider"
import { AppLocalization } from "../localization/appLocalization"

type MoneyFactoryOptions = Readonly<{
    locale: string
    l10n: AppLocalization
}>

export class MoneyFactory {
    public readonly locale: string
    private readonly l10n: AppLocalization
    private formatter: (amount: Dinero<number>) => string
    private numberFormatter: (amount: Dinero<number>) => string

    constructor({ locale, l10n }: MoneyFactoryOptions) {
        this.locale = locale
        this.l10n = l10n
        this.formatter = createRoundingMoneyFormatter(locale)
        this.numberFormatter = createRoundingNumberFormatter(locale)
    }

    isCurrencyPrefixed(currency: Currency<number>) {
        const formatter = Intl.NumberFormat(this.locale, { style: "currency", currency: currency.code })
        const probe = formatter.format(10)
        return probe.slice(0, 2) !== "10"
    }

    getLocalizedCurrencySymbol(currency: Currency<number>) {
        const formatter = Intl.NumberFormat(this.locale, { style: "currency", currency: currency.code })
        const parts = formatter.formatToParts(10)
        return parts.find((p) => p.type === "currency")?.value ?? currency.code
    }

    // Gets the amount for instance 100 dk based on language
    format(amount: Dinero<number>): string {
        return this.formatter(amount)
    }

    // removes currency and gets the amount only
    formatAsNumber(amount: Dinero<number>) {
        return this.numberFormatter(amount)
    }

    formatAsNumberWithScale(amount: Dinero<number>, scale: "currency" | "value") {
        if (scale === "currency") {
            return this.formatAsNumber(amount)
        } else {
            const formatter = createRoundingNumberFormatter(this.locale, scale)
            return formatter(amount)
        }
    }

    formatWithUnit(amount: Dinero<number>, unit: AmountUnit) {
        // Like: money-amount-unit-month etc
        const unitLabel = this.l10n.getStringForEnum("money-amount-unit", unit, unit)
        const amountLabel = this.formatter(amount)
        return `${amountLabel} / ${unitLabel}`
    }

    // Format compactly with rounding logic (68,301 and 4,213.63)
    formatCompact(amount: Dinero<number>): string {
        if (toDecimalParts(amount).value >= 10000) {
            const roundedAmount = transformScale(amount, 0)
            return this.formatNumberOnly(roundedAmount)
        }

        return this.formatNumberOnly(amount)
    }

    formatNumberOnly(amount: Dinero<number>): string {
        const { value, scale } = toDecimalParts(amount)

        return value.toLocaleString(this.locale, {
            minimumFractionDigits: scale,
            maximumFractionDigits: scale,
            useGrouping: true,
        })
    }
}

type UseMoneyOptions = Readonly<{
    locale?: string
}>

export function useMoney({ locale }: UseMoneyOptions) {
    const selectedLocale = useAppSelector(selectSelectedLocale)
    const localeToUse = locale ?? selectedLocale
    const { l10n } = useAppLocalization()
    const factory = useMemo(() => new MoneyFactory({ locale: localeToUse, l10n }), [localeToUse, l10n])

    return factory
}
